import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Loader2 } from 'lucide-react';
import {
  CATEGORY_COLORS,
  createDocumentCategory,
  deleteDocumentCategory,
  listDocumentCategories,
  updateDocumentCategory,
  type DocumentCategory,
} from '../../lib/documents';

interface CategoryManagerModalProps {
  businessId: string;
  isOpen: boolean;
  onClose: () => void;
  onChanged: () => void;
}

export default function CategoryManagerModal({ businessId, isOpen, onClose, onChanged }: CategoryManagerModalProps) {
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setCategories(await listDocumentCategories(businessId)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (isOpen) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [isOpen, businessId]);

  const add = async () => {
    if (!newName.trim() || busy) return;
    setBusy(true);
    try {
      const color = CATEGORY_COLORS[categories.length % CATEGORY_COLORS.length];
      const cat = await createDocumentCategory(businessId, newName.trim(), color);
      setCategories((prev) => [...prev, cat]);
      setNewName('');
      onChanged();
    } finally { setBusy(false); }
  };

  const rename = async (id: string, name: string) => {
    setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, name } : c)));
  };
  const commitRename = async (id: string, name: string) => {
    try { await updateDocumentCategory(id, { name: name.trim() || 'Untitled' }); onChanged(); } catch (e) { console.error(e); }
  };
  const recolor = async (id: string, color: string) => {
    setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, color } : c)));
    try { await updateDocumentCategory(id, { color }); onChanged(); } catch (e) { console.error(e); }
  };
  const remove = async (id: string, name: string) => {
    if (!confirm(`Delete category "${name}"? Documents in it become uncategorized.`)) return;
    try { await deleteDocumentCategory(id); setCategories((prev) => prev.filter((c) => c.id !== id)); onChanged(); } catch (e) { console.error(e); }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100100] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-[16px] font-semibold text-gray-900">Manage categories</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {loading ? (
            <div className="flex justify-center py-8 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : categories.length === 0 ? (
            <div className="text-center text-[13px] text-gray-400 py-6">No categories yet. Add one below.</div>
          ) : (
            categories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  {CATEGORY_COLORS.map((c) => (
                    <button key={c} onClick={() => recolor(cat.id, c)} className={`h-4 w-4 rounded-full transition-transform ${cat.color === c ? 'ring-2 ring-offset-1 ring-gray-400 scale-110' : ''}`} style={{ backgroundColor: c }} />
                  ))}
                </div>
                <input
                  value={cat.name}
                  onChange={(e) => rename(cat.id, e.target.value)}
                  onBlur={(e) => commitRename(cat.id, e.target.value)}
                  className="flex-1 text-[14px] border border-gray-200 rounded-lg px-2.5 py-1.5"
                />
                <button onClick={() => remove(cat.id, cat.name)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex items-center gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            placeholder="New category name"
            className="flex-1 text-[14px] border border-gray-200 rounded-lg px-3 py-2"
          />
          <button onClick={add} disabled={busy || !newName.trim()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-50">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
